import type { Task } from "./entities";
import { isLive, type Dataset, type OperationFactory, type SyncOperation } from "./operations";

/** A Task's parent when it is a sub-task whose parent still exists. */
export function parentTask(dataset: Dataset, task: Task): Task | undefined {
  const parent = task.parentId ? dataset.tasks[task.parentId] : undefined;
  return parent && isLive(parent) ? parent : undefined;
}

/** A Task's live sub-tasks, by name. */
export function subTasks(dataset: Dataset, taskId: string): Task[] {
  return Object.values(dataset.tasks).filter((task) => isLive(task) && task.parentId === taskId).sort((a, b) => a.name.localeCompare(b.name));
}

/** Whether a Task can be picked for new time: open, and under an open parent when it is a sub-task. */
export function isTaskOpen(dataset: Dataset, task: Task): boolean {
  if (!isLive(task) || task.doneAt !== null || task.archivedAt !== null) return false;
  if (task.parentId === null) return true;
  const parent = parentTask(dataset, task);
  return parent !== undefined && parent.doneAt === null && parent.archivedAt === null;
}

/** "Checkout › Refund flow" for a sub-task; a Task's own name otherwise. */
export function taskLabel(dataset: Dataset, task: Task): string {
  const parent = parentTask(dataset, task);
  return parent ? `${parent.name} › ${task.name}` : task.name;
}

/** The open sub-task to pick up after this one: the next by name, wrapping to the first. */
export function nextSubTask(dataset: Dataset, task: Task): Task | undefined {
  const parent = parentTask(dataset, task);
  if (!parent) return undefined;
  const siblings = subTasks(dataset, parent.id);
  const index = siblings.findIndex((sibling) => sibling.id === task.id);
  return [...siblings.slice(index + 1), ...siblings.slice(0, index)].find((sibling) => isTaskOpen(dataset, sibling));
}

/** A Project's live Tasks that pass `keep`, each followed by its sub-tasks that do. */
export function orderedTasks(dataset: Dataset, projectId: string, keep: (task: Task) => boolean = () => true): Task[] {
  const tasks = Object.values(dataset.tasks).filter((task) => isLive(task) && task.projectId === projectId).sort((a, b) => a.name.localeCompare(b.name));
  const ordered: Task[] = [];
  for (const task of tasks) {
    if (parentTask(dataset, task)) continue;
    if (keep(task)) ordered.push(task);
    for (const child of tasks) if (child.parentId === task.id && keep(child)) ordered.push(child);
  }
  return ordered;
}

/**
 * Marks a Task done or reopens it. Completing a Task completes its open sub-tasks with it;
 * reopening it reopens the ones that were completed along with it.
 */
export function setTaskDone(dataset: Dataset, factory: OperationFactory, taskId: string, done: boolean, now: number): SyncOperation[] {
  const task = dataset.tasks[taskId];
  if (!task) return [];
  const doneAt = done ? now : null;
  const children = subTasks(dataset, taskId).filter((child) => done ? child.doneAt === null : task.doneAt !== null && child.doneAt === task.doneAt);
  return [factory.op("task", taskId, { doneAt }), ...children.map((child) => factory.op("task", child.id, { doneAt }))];
}

/** Moves a Task to Trash with its sub-tasks, so they come back together. */
export function trashTask(dataset: Dataset, factory: OperationFactory, taskId: string, now: number): SyncOperation[] {
  return [factory.op("task", taskId, { deletedAt: now }), ...subTasks(dataset, taskId).map((child) => factory.op("task", child.id, { deletedAt: now }))];
}
